// askSmart-ui/src/pages/MapleCoin/manage/adminApi.ts
//
// The read-only MapleCoin Admin (customer servicing) API. The signed-in
// session is the only identity sent; the server takes the Admin's identity
// for the access audit from the verified token. Opening a customer, an
// account's activity or a transaction is recorded by the server.
import { Currency, MinorString, TransactionDirection, TransactionStatus } from '../types';

export class AdminApiError extends Error {
  status: number;
  code: string | null;
  constructor(status: number, code: string | null) {
    super(`MapleCoin admin request failed (${status})`);
    this.name = 'AdminApiError';
    this.status = status;
    this.code = code;
  }
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/maplecoin/admin${path}`, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new AdminApiError(res.status, typeof body?.error === 'string' ? body.error : null);
  }
  return (await res.json()) as T;
}

export interface CustomerCard {
  customerNumber: string;
  displayName: string;
  legalName: string;
  customerType: 'INDIVIDUAL' | 'BUSINESS';
  status: string;
  accounts: { accountNumber: string; currency: Currency; status: string }[];
}

export interface SearchResponse {
  kind: 'CUSTOMER_NUMBER' | 'ACCOUNT_NUMBER' | 'NAME';
  truncated: boolean;
  results: CustomerCard[];
}

export type KycStatus = 'NOT_VERIFIED' | 'VERIFIED' | 'REVIEW_REQUIRED';

export interface KycEvent {
  status: KycStatus;
  provider: string | null;
  providerReference: string | null;
  verifiedAt: string | null;
  reverificationDueAt: string | null;
  reason: string | null;
  recordedAt: string;
}

export interface KycSummary {
  status: KycStatus;
  onRecord: boolean;
  provider: string | null;
  verifiedAt: string | null;
  lastVerifiedAt: string | null;
  lastVerifiedProvider: string | null;
  reverificationDueAt: string | null;
  history: KycEvent[];
}

/** A hold or restriction as Admin sees it (view-only; no free-text reasons). */
export interface AdminControl {
  reference: string;
  amountMinor?: MinorString;
  currency?: Currency;
  expiresAt?: string | null;
  restrictionType?: 'NO_OUTGOING' | 'NO_INCOMING';
  reasonCode: 'FRAUD_REVIEW' | 'COMPLIANCE_REVIEW' | 'OPERATIONAL';
  status: 'ACTIVE' | 'RELEASED' | 'EXPIRED';
  placedAt: string;
  placedByType: string | null;
  releasedAt: string | null;
}

export interface AdminAccount {
  accountNumber: string;
  currency: Currency;
  status: 'OPEN' | 'FROZEN' | 'CLOSED';
  openedAt: string;
  /** Cleared balance (holds not deducted). */
  availableMinor: MinorString;
  heldMinor?: MinorString;
  /** What the customer can use now: balance − holds. */
  spendableMinor?: MinorString;
  pendingMinor: MinorString;
  holds?: AdminControl[];
  restrictions?: AdminControl[];
}

export interface CustomerRecord {
  customer: { customerNumber: string; customerType: string; displayName: string; legalName: string; status: string };
  kyc: KycSummary;
  accounts: AdminAccount[];
}

export interface AdminTransaction {
  reference: string;
  type: string;
  direction: TransactionDirection;
  status: TransactionStatus;
  amountMinor: MinorString;
  currency: Currency;
  createdAt: string;
  statusChangedAt: string;
  reversesReference: string | null;
  reversedByReference: string | null;
  reason?: string | null;
}

export interface TransactionDetail extends AdminTransaction {
  customerNumber: string;
  accountNumber: string;
  rail: string;
  externalSource: string;
  externalReference: string;
  reason: string | null;
  events: { fromStatus: TransactionStatus | null; toStatus: TransactionStatus; action: string; actorType: string;
    reason: string | null; occurredAt: string }[];
}

export const searchCustomers = (q: string) => get<SearchResponse>(`/customers/search?q=${encodeURIComponent(q)}`);
export const getCustomer = (customerNumber: string) => get<CustomerRecord>(`/customers/${encodeURIComponent(customerNumber)}`);
export const getTransaction = (reference: string) => get<TransactionDetail>(`/transactions/${encodeURIComponent(reference)}`);

export function getActivity(accountNumber: string, opts: { limit?: number; before?: string } = {}) {
  const q = new URLSearchParams();
  if (opts.limit) q.set('limit', String(opts.limit));
  if (opts.before) q.set('before', opts.before);
  const qs = q.toString();
  return get<{ accountNumber: string; transactions: AdminTransaction[]; nextBefore: string | null }>(
    `/accounts/${encodeURIComponent(accountNumber)}/activity${qs ? `?${qs}` : ''}`,
  );
}
